import { useEffect, useMemo } from 'react'

import { Alert, Button, Checkbox, HStack, Icon, Menu, Portal, Text, VStack, useDisclosure } from '@chakra-ui/react'
import { ChevronDownIcon, HelpCircleIcon } from '@editor/assets/icons'
import TutorialVideoModal from '@editor/components/TutorialVideoModal'
import { EDITOR_TOOLS } from '@editor/config/constants'
import { InspectionItemType, VolumeEstimationMethods } from '@editor/interfaces/inspection'
import { forceOpenToolPanel } from '@editor/pages/projects/editor/store/editor'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

import { Mode } from '../../utils'
import { setEstimationMethod, setRebarModelledConfirmed } from '../store/volumeEstimation'

const EstimationMethod = ({ mode }: { mode: Mode }) => {
  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const videos = useSelector((root: RootState) => root.videos.videos)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const estimationMethod = useSelector((state: RootState) => state.toolVolumeEstimationPolygon.estimationMethod)
  const rebarModelledConfirmed = useSelector(
    (state: RootState) => state.toolVolumeEstimationPolygon.rebarModelledConfirmed,
  )

  // Derived
  const selectedVolumes = useMemo(
    () =>
      inspectionItems.filter(
        (item) =>
          item.item_type === InspectionItemType.Volume &&
          selectedElementIds.find((id) => item.shape_ids.polygons.includes(id)),
      ),
    [selectedElementIds, inspectionItems],
  )
  const selectedVolume = selectedVolumes[0]
  const isSelectedMultiple = selectedVolumes.length > 1

  // Video modal
  const useDisclosureProps = useDisclosure()
  const videoConfig = useMemo(
    () =>
      videos.find(
        (video) => video.toolType === EDITOR_TOOLS.VOLUME_POLYGON && video.variant === 'volume-estimation-method',
      ),
    [videos],
  )

  /**
   * Changed selected method based on the selected volume or
   * set to Standard if user is on Volume tool.
   */
  useEffect(() => {
    if (mode === Mode.None) return

    if (mode === Mode.Modelling) {
      dispatch(setEstimationMethod(VolumeEstimationMethods.Standard))
      return
    }

    const method =
      selectedVolume?.volume?.is_flat_plane === false
        ? VolumeEstimationMethods.Advanced
        : VolumeEstimationMethods.Standard
    dispatch(setEstimationMethod(method))

    // If selected method is Standard but marked as not a valid method, open the panel
    if (method === VolumeEstimationMethods.Standard && selectedVolume?.volume?.is_valid_method === false) {
      dispatch(forceOpenToolPanel())
    }
  }, [selectedVolume, mode, dispatch])

  // Only for when volume is selected or tool is selected
  if (mode === Mode.None) {
    return null
  }

  return (
    <VStack width="100%">
      <Text fontSize="md" color="gray2.300" alignSelf="start">
        体積測定方法
      </Text>

      <VStack width="95%" alignSelf="start" gap={3}>
        <HStack width="100%">
          <Icon as={HelpCircleIcon} boxSize="1.2em" color="gray2.400" />
          <Button
            variant="ghost"
            color="gray2.400"
            whiteSpace="normal"
            textAlign="left"
            alignSelf="start"
            onClick={() => useDisclosureProps.onOpen()}
            fontSize="11px"
            borderBottom="1px dotted {colors.gray2.500}"
            borderRadius={0}
            p={0}
            height="auto"
            _hover={{
              background: 'transparent',
              textDecoration: 'none',
              borderBottom: '1px solid {colors.gray2.300}',
              color: 'gray2.300',
            }}
          >
            体積測定の詳細はこちら
          </Button>
        </HStack>

        {mode === Mode.Reevaluation && (
          <>
            {isSelectedMultiple && <Alert.Root variant="panel-warning">1つの体積だけを選択してください。</Alert.Root>}

            {selectedVolume?.volume?.is_flat_plane === true && selectedVolume?.volume?.is_valid_method === false && (
              <Alert.Root variant="panel-info">
                斫り面が平坦ではないと検出しました。体積測定方法の設定を変更することで、精度が向上する可能性あります。
              </Alert.Root>
            )}
          </>
        )}

        <Menu.Root variant="editorPanel" positioning={{ placement: 'top-start', gutter: 4, sameWidth: true }}>
          <Menu.Trigger asChild>
            <Button variant="panel-dropdown" justifyContent="space-between">
              {estimationMethod}の測定方法
              <ChevronDownIcon />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content bg="gray2.700">
                <Menu.Item
                  value="standard"
                  backgroundColor={estimationMethod === VolumeEstimationMethods.Standard ? 'secondary.600' : undefined}
                  onClick={() => dispatch(setEstimationMethod(VolumeEstimationMethods.Standard))}
                >
                  {VolumeEstimationMethods.Standard}（平坦な斫り面）
                </Menu.Item>
                <Menu.Item
                  value="advanced"
                  backgroundColor={estimationMethod === VolumeEstimationMethods.Advanced ? 'secondary.600' : undefined}
                  onClick={() => dispatch(setEstimationMethod(VolumeEstimationMethods.Advanced))}
                >
                  {VolumeEstimationMethods.Advanced}（平坦ではない斫り面）
                </Menu.Item>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>

        {estimationMethod === VolumeEstimationMethods.Advanced && (
          <Checkbox.Root
            variant="panel"
            checked={rebarModelledConfirmed}
            onCheckedChange={(e) => dispatch(setRebarModelledConfirmed(e.checked === true))}
            required
            width="100%"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            <Checkbox.Label>
              <Text fontSize="xs">鉄筋がモデリング済み</Text>
            </Checkbox.Label>
          </Checkbox.Root>
        )}
      </VStack>

      {videoConfig && <TutorialVideoModal useDisclosureProps={useDisclosureProps} {...videoConfig} />}
    </VStack>
  )
}

export default EstimationMethod
